
import { OptionalColumnEnum } from "../../common/enums/DRCXEnums";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { ObjectFeaturesEnum } from "../../common/enums/ObjectFeaturesEnum";
import { TrendConfigurationTypeEnum } from "../../common/enums/TrendConfigurationTypeEnum";
import { DRCXAvailableColumns } from "../../common/models/drcx/DRCXAvailableColumns";
import { IDRCXAvailableColumnsDTO } from "../../common/models/drcx/DRCXAvailableColumnsDTO";
import { DRCXAvailableObject } from "../../common/models/drcx/DRCXAvailableObject";
import { DRCXAvailableObjects } from "../../common/models/drcx/DRCXAvailableObjects";
import { IDRCXAvailableObjectsDTO } from "../../common/models/drcx/DRCXAvailableObjectsDTO";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { RouteFromObjectType } from "../../common/utilities/RouteFromObjectType";
import { GetAvailableColumnsOptions, GetAvailableObjectOptions } from "../drcx-configuration-data-service/Models";
import OfflineDataService from "../offline-data-service/OfflineDataService";
import restService from "../rest-service/RestService";
import { GraphConfigurationData, IGraphConfigurationDataDTO } from "../trending-data-service/TrendConfigurationModels";
import { UIChartPropertiesClient } from "../trending-data-service/UIChartProperties";
import { GraphDefaultModel, IGraphDefaultOptions, ITrendConfigurationDTO, TrendConfigurationModel } from "./Models";


export interface ITrendConfigurationTypeQueryParams {
    configurationType: TrendConfigurationTypeEnum;
//    deviceType?: DeviceTypesEnum
}


const TrendingConfigurationDataService = {

    /*

    public isConfigurationNameValid(contextGroupID: number, eLynxObject: Models.IeLynxObjectModel, configurationName: string, configurationId: number): Services.RestPromise<boolean> {

        let path: string = "";

        switch (eLynxObject.objectType) {

            case Enums.eLynxObjectTypeEnum.Device:

                path =
                    [
                        "groups",
                        contextGroupID,
                        "devices",
                        eLynxObject.id,
                        "trendconfig",
                        "isvalidname"
                    ].join("/");

                break;

            case Enums.eLynxObjectTypeEnum.Group:
                path =
                    [
                        "groups",
                        contextGroupID,
                        "trendconfig",
                        "isvalidname"
                    ].join("/");

                break;

            case Enums.eLynxObjectTypeEnum.Well:

                path =
                    [
                        "groups",
                        contextGroupID,
                        "wells",
                        eLynxObject.id,
                        "trendconfig",
                        "isvalidname"
                    ].join("/");

                break;
        }

        const getParams: Models.ITrendConfigurationNameValidQueryParams = {
            configurationID: configurationId > 0 ? configurationId : null,
            configurationName: configurationName
        };

        return this._restService
            .get<boolean, Models.ITrendConfigurationNameValidQueryParams>(path, getParams)
            .then((data) => { return data.entity });
    }
 */

    getAvailableColumns: async function (
        groupId: number,
        objects: Array<IeLynxObject>,
        dataType: eLynxObjectTypeEnum,
        signal?: AbortSignal
    ): Promise<DRCXAvailableColumns> {

        const path: string = `groups/${groupId}/trendconfig/availableColumns`;

        const response = await restService
            .post<GetAvailableColumnsOptions, IDRCXAvailableColumnsDTO>(
                path,
                {
                    objects: objects.map(o => {
                        return {
                            id: o.id,
                            objectType: o.objectType
                        };
                    }),
                    dataType: dataType
                }
                ,
                signal);

        return new DRCXAvailableColumns(response.data);
    },
    getAvailableObjects: async function (
        customerID: number,
        groupId: number,
        includeDescendentGroups: boolean = true,
        includeDevices: boolean = true,
        includeTanks: boolean = true,
        includeTankBatteries: boolean = false,
        includeWells: boolean = true,
        offline: boolean,
        features?: ObjectFeaturesEnum,
        signal?: AbortSignal
    ): Promise<DRCXAvailableObjects> {

        if (!offline) {

            const path: string = `groups/${groupId}/trendconfig/availableObjects`;

            const response = await restService.get<IDRCXAvailableObjectsDTO, GetAvailableObjectOptions>(
                path,
                signal,
                {
                    includeDevices: includeDevices,
                    includeTanks: includeTanks,
                    includeWells: includeWells,
                    includeTankBatteries: includeTankBatteries,
                    includeDescendentGroups: includeDescendentGroups
                });

            return new DRCXAvailableObjects(response.data);
        } else {
            const p1 = OfflineDataService.getGroupDevices(customerID, groupId, true, features);
            const p2 = OfflineDataService.getGroupWells(customerID, groupId, features);
            const p3 = OfflineDataService.getGroupTankBatteries(customerID, groupId, features);

            let [devices, wells, tankBatteries] = await Promise.all([p1, p2, p3]);

            const availableData = new DRCXAvailableObjects({
                groups: [],
                devices: [],
                tankBatteries: [],
                tanks: [],
                wells: []
            });

            availableData.devices = devices.filter(o => o.deviceTypeID !== 1000)
                .map(o => new DRCXAvailableObject(o.name, o.id, eLynxObjectTypeEnum.Device, undefined, undefined, true))
                .sort((a, b) => (a.displayText as string).localeCompare(b.displayText as string));
            availableData.tanks = devices.filter(o => o.deviceTypeID === 1000)
                .map(o => new DRCXAvailableObject(o.name, o.id, eLynxObjectTypeEnum.Tank, undefined, undefined, true))
                .sort((a, b) => (a.displayText as string).localeCompare(b.displayText as string));
            availableData.wells = wells.map(o => new DRCXAvailableObject(o.name, o.id, eLynxObjectTypeEnum.Well, undefined, undefined, true))
                .sort((a, b) => (a.displayText as string).localeCompare(b.displayText as string));
            availableData.tankBatteries = tankBatteries.map(o => new DRCXAvailableObject(o.name, o.id, eLynxObjectTypeEnum.TankBattery, undefined, undefined, true))
                .sort((a, b) => (a.displayText as string).localeCompare(b.displayText as string));

            return availableData;
        }
    },
    getChartProperties: async function (
        groupID: number,
        eLynxObject: IeLynxObject,
        model: TrendConfigurationModel | undefined,
        signal?: AbortSignal):
        Promise<UIChartPropertiesClient | undefined> {

        if (model) {

            const path = eLynxObject.objectType === eLynxObjectTypeEnum.Group ?
                `groups/${groupID}/trendconfig/chartproperties` :
                `groups/${groupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/trendconfig/chartproperties`;

            // Is this necessary???
            // Make sure the series and y-Axis order values are in sequence.
            //model.uiChartProperties && model.uiChartProperties.setOrder();

            if (model.uiChartProperties) {
                model.uiChartProperties.series = model.uiChartProperties.series?.sort((a, b) => (a.yAxis ?? 0) - (b.yAxis ?? 0));
            }

            const response = await restService
                .post<TrendConfigurationModel, UIChartPropertiesClient>(
                    path, model, signal);

            return new UIChartPropertiesClient(response.data);
        } else {
            return undefined;
        }
    },
    getConfiguration: async function (
        contextGroupID: number,
        eLynxObject: IeLynxObject,
        configurationId?: number,
        signal?: AbortSignal):
        Promise<TrendConfigurationModel> {

        const path = eLynxObject.objectType === eLynxObjectTypeEnum.Group ?
            `groups/${contextGroupID}/trendconfigs/${configurationId}` :
            `groups/${contextGroupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/trendconfigs/${configurationId}`;

        const result = await restService.get<ITrendConfigurationDTO>(path, signal);
        return new TrendConfigurationModel(result.data);
    },
    getConfigurationDefault: async function (
        contextGroupID: number,
        eLynxObject: IeLynxObject,
        configurationID?: number,
        signal?: AbortSignal):
        Promise<IGraphDefaultOptions> {

        const path = eLynxObject.objectType === eLynxObjectTypeEnum.Group ?
            `groups/${contextGroupID}/trending/configurationdefault` :
            `groups/${contextGroupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/trending/configurationdefault`;

        const response = await restService.get<IGraphDefaultOptions, {
            configurationID?: number;
        }>(path, signal, { configurationID: configurationID });

        return response.data;
    },
    getConfigurationList: async function (contextGroupID: number, eLynxObject: IeLynxObject, signal?: AbortSignal):
        Promise<GraphConfigurationData> {

        const path = eLynxObject.objectType === eLynxObjectTypeEnum.Group ?
            `groups/${contextGroupID}/trending/configlist` :
            `groups/${contextGroupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/trending/configlist`;

        const result = await restService.get<IGraphConfigurationDataDTO>(path, signal);
        return new GraphConfigurationData(result.data);
    },
    getAllConfigurations: async function (contextGroupID: number, configurationType: TrendConfigurationTypeEnum, signal?: AbortSignal):
        Promise<GraphConfigurationData> {

        const path: string = `/groups/${contextGroupID}/trending/configurations/all`;

        const queryParams: ITrendConfigurationTypeQueryParams = {
            configurationType: configurationType
        };

        const response = await restService.get<IGraphConfigurationDataDTO, ITrendConfigurationTypeQueryParams>(path, signal, queryParams);
        return new GraphConfigurationData(response.data);            
    },

    saveConfiguration: async function (contextGroupID: number, eLynxObject: IeLynxObject,
        model: TrendConfigurationModel, signal?: AbortSignal): Promise<TrendConfigurationModel> {

        // make sure attribute schema key is set correctly
        const configuration = {...model} as TrendConfigurationModel;
        configuration.sections.forEach(o => {
            o.columns.filter(o => o.optionalColumn === OptionalColumnEnum.DeviceAttributeSchema ||
                                o.optionalColumn === OptionalColumnEnum.WellAttributeSchema ||
                                o.optionalColumn === OptionalColumnEnum.TankAttributeSchema ||
                                o.optionalColumn === OptionalColumnEnum.TagAttributeSchema).forEach(v => {
                                    if (v.id !== undefined) {
                                        v.key = v.id.toString();
                                    }
                                });
        });

        // Is this necessary???
        // Make sure the series and y-Axis order values are in sequence.
        model.uiChartProperties && model.uiChartProperties.setOrder();

        const path = eLynxObject.objectType === eLynxObjectTypeEnum.Group ?
            `groups/${contextGroupID}/trendconfig` :
            `groups/${contextGroupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/trendconfig`;

        const response = await restService.put<ITrendConfigurationDTO, ITrendConfigurationDTO>(path, model.toDTO(), signal);

        return new TrendConfigurationModel({
            ...model.toDTO(),
            ...response.data
        });
    },
    setConfigurationDefault: async function (contextGroupID: number, eLynxObject: IeLynxObject,
        model: GraphDefaultModel, signal?: AbortSignal): Promise<void> {

        const path = eLynxObject.objectType === eLynxObjectTypeEnum.Group ?
            `groups/${contextGroupID}/trendingdefault/save` :
            `groups/${contextGroupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/trendingdefault/save`;

        await restService.put<GraphDefaultModel, void>(path, model, signal);
    },
    deleteConfiguration: async function (contextGroupID: number, eLynxObject: IeLynxObject, 
        model: TrendConfigurationModel, signal?: AbortSignal
    ): Promise<void> {

        const path = eLynxObject.objectType === eLynxObjectTypeEnum.Group ?
            `groups/${contextGroupID}/trendconfig` :
            `groups/${contextGroupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/trendconfig`;

        await restService.delete<ITrendConfigurationDTO, null>(path, signal, null, model.toDTO());
    },
    hideConfigurations: async function (groupID: number, configurationType: TrendConfigurationTypeEnum, idList: number [], hide: boolean): Promise<void> {
        const path = `groups/${groupID}/trending/configurations/${configurationType}/${hide ? 'hide' : 'unhide'}`;
        await restService.post<number[], void>(path, idList);
    },
    adminDeleteConfigurations: async function (groupID: number, configurationType: TrendConfigurationTypeEnum, idList: number []): Promise<void> {
        const path = `groups/${groupID}/trending/configurations/${configurationType}`;
        await restService.delete<number[], undefined>(path, undefined, undefined, idList);
    },

};
    
export default TrendingConfigurationDataService;